<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { animate } from 'motion'
import { ArrowRight, ChevronLeft, ListChecks, Rows3, ShieldAlert, Type, Volume2, VolumeX, X } from '@lucide/vue'
import { ApiError, api, postJson, putJson } from '../api'
import SurveyQuestionRenderer from '../components/SurveyQuestionRenderer.vue'
import { openFontLibrary } from '../font'
import { music, openMusicLibrary } from '../music'
import { playTone } from '../sound'
import { preferences } from '../state'
import { auth } from '../state'
import type { SurveyDetail, SurveyQuestion } from '../types'

const route = useRoute()
const router = useRouter()
const survey = ref<SurveyDetail | null>(null)
const current = ref(0)
const answers = ref<Record<string, unknown>>({})
const startedAt = Date.now()
const submitting = ref(false)
const error = ref('')
const feedbackBurst = ref(false)
const variant = ref<'A' | 'B'>('A')
const crisis = ref<{ severity: string; resourcesRequired: boolean } | null>(null)
const crisisResources = ref<{ immediateDanger: string; hotline: { name: string; phone: string }; note: string } | null>(null)
const pendingResult = ref<{ responseId: string; resultToken?: string } | null>(null)
const dirty = ref(false)
const completed = ref(false)
const draftSaved = ref(false)
const saveStatus = ref<'local' | 'syncing' | 'synced' | 'offline'>('local')
const resumedFromDraft = ref(false)
const sessionEntries = ref(1)
const answerArea = ref<HTMLElement | null>(null)
const questionStage = ref<HTMLElement | null>(null)
const navigatorOpen = ref(false)
const fillMode = ref<'paged' | 'scroll'>('paged')
let saveTimer: number | undefined

function answered(value: unknown) { return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0) }
function ruleMatches(answer: unknown, operator: string, expected: unknown) {
  if (operator === 'equals') return answer === expected
  if (operator === 'not_equals') return answer !== expected
  if (operator === 'includes') return Array.isArray(answer) ? answer.includes(expected) : String(answer ?? '').includes(String(expected ?? ''))
  if (operator === 'not_includes') return Array.isArray(answer) ? !answer.includes(expected) : !String(answer ?? '').includes(String(expected ?? ''))
  if (operator === 'greater_than') return Number(answer) > Number(expected)
  if (operator === 'less_than') return Number(answer) < Number(expected)
  if (operator === 'answered') return answered(answer)
  if (operator === 'not_answered') return !answered(answer)
  return false
}
const visibleQuestions = computed(() => survey.value?.definition?.questions.filter((q) => {
  if (!q.visibility?.rules.length) return true
  const results = q.visibility.rules.map((rule) => ruleMatches(answers.value[rule.questionId], rule.operator, rule.value))
  return q.visibility.mode === 'all' ? results.every(Boolean) : results.some(Boolean)
}) ?? [])
const question = computed(() => visibleQuestions.value[current.value])
const total = computed(() => visibleQuestions.value.length)
const experienceMode = computed(() => survey.value?.definition?.presentation?.mode ?? (survey.value?.type === 'research' || survey.value?.type === 'sensitive' ? 'neutral' : 'heartlight'))
const saveStatusText = computed(() => {
  if (saveStatus.value === 'syncing') return '正在同步到服务器…'
  if (saveStatus.value === 'synced') return '已同步到服务器'
  if (saveStatus.value === 'offline') return '网络不可用，已安全保存到本机'
  return '已保存到本机'
})
function questionComplete(item: SurveyQuestion) {
  const value = answers.value[item.id]
  if (item.type === 'matrix_single' || item.type === 'matrix_multi') {
    return Boolean(value && typeof value === 'object' && item.rows?.every((row) => answered((value as Record<string, unknown>)[row.value])))
  }
  if (item.type === 'ranking') return Array.isArray(value) && value.length === item.options?.length
  return answered(value)
}
const answeredCount = computed(() => visibleQuestions.value.filter(questionComplete).length)
const progress = computed(() => total.value ? (answeredCount.value / total.value) * 100 : 0)
const incompleteRequiredIndexes = computed(() => visibleQuestions.value.flatMap((item, index) => item.required && !questionComplete(item) ? [index] : []))
const canContinue = computed(() => {
  if (!question.value?.required) return true
  return questionComplete(question.value)
})
const missingRequiredCount = computed(() => {
  if (!question.value?.required || canContinue.value) return 0
  const value = answers.value[question.value.id]
  if (question.value.type === 'matrix_single' || question.value.type === 'matrix_multi') {
    const matrix = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    return question.value.rows?.filter((row) => !answered(matrix[row.value])).length ?? 0
  }
  if (question.value.type === 'ranking') {
    return Math.max(0, (question.value.options?.length ?? 0) - (Array.isArray(value) ? value.length : 0))
  }
  return 1
})
const continuationHint = computed(() => {
  if (canContinue.value) return current.value === total.value - 1 ? '本题已完成，可以提交' : '本题已完成，可继续或跳题'
  if (question.value?.type === 'matrix_single' || question.value?.type === 'matrix_multi') return `本题还差 ${missingRequiredCount.value} 项评分，可先跳题`
  if (question.value?.type === 'ranking') return `本题还差 ${missingRequiredCount.value} 个排序，可先跳题`
  return '本题未完成，可先跳到其他题'
})
const storageKey = computed(() => `lumi-draft-${survey.value?.id || route.params.slug}`)

onMounted(async () => {
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('keydown', handleKeyboard)
  try {
    const loaded = await api<SurveyDetail>(`/api/surveys/${route.params.slug}`)
    if (loaded.locked || !loaded.definition) return router.replace(`/surveys/${route.params.slug}`)
    survey.value = loaded
    const storedFillMode = localStorage.getItem(`lumi-fill-mode-${loaded.id}`)
    fillMode.value = storedFillMode === 'paged' || storedFillMode === 'scroll'
      ? storedFillMode
      : loaded.definition.presentation?.navigationMode ?? 'paged'
  const entryKey = `lumi-session-entries-${loaded.id}`
  sessionEntries.value = Math.min(100, Number(localStorage.getItem(entryKey) || 0) + 1)
  localStorage.setItem(entryKey, String(sessionEntries.value))
  const storedVariant = localStorage.getItem(`lumi-variant-${loaded.id}`)
  variant.value = loaded.definition.experiment?.enabled ? (storedVariant === 'B' ? 'B' : storedVariant === 'A' ? 'A' : Math.random() > 0.5 ? 'B' : 'A') : 'A'
  localStorage.setItem(`lumi-variant-${loaded.id}`, variant.value)
    const saved = localStorage.getItem(storageKey.value)
    if (saved) try { answers.value = JSON.parse(saved); draftSaved.value = true; resumedFromDraft.value = true; saveStatus.value = 'local' } catch { /* ignore damaged local draft */ }
    if (auth.user && loaded.allowSaveProgress) {
      const remote = await api<{ draft: Record<string, unknown> }>(`/api/surveys/${loaded.id}/draft`)
      if (Object.keys(remote.draft).length) {
        answers.value = { ...answers.value, ...remote.draft }
        resumedFromDraft.value = true
        saveStatus.value = 'synced'
      }
    }
  } catch (err) { error.value = err instanceof ApiError ? err.message : '问卷加载失败。' }
})

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (!dirty.value || completed.value) return
  event.preventDefault()
  event.returnValue = ''
}

function handleKeyboard(event: KeyboardEvent) {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return
  if (fillMode.value === 'scroll') return
  if (event.key === 'ArrowLeft') void move(-1)
  if (event.key === 'ArrowRight') void move(1)
}

function confirmExit(event: MouseEvent) {
  if (!dirty.value || completed.value) return
  if (!window.confirm('当前进度已经保存在本机浏览器中，确定要离开问卷吗？')) event.preventDefault()
}

watch(answers, (value) => {
  if (!survey.value) return
  try {
    localStorage.setItem(storageKey.value, JSON.stringify(value))
    draftSaved.value = true
    saveStatus.value = navigator.onLine ? 'local' : 'offline'
  } catch { /* storage may be disabled */ }
  dirty.value = true
  window.clearTimeout(saveTimer)
  if (auth.user && survey.value.allowSaveProgress) {
    saveStatus.value = navigator.onLine ? 'syncing' : 'offline'
    saveTimer = window.setTimeout(() => void putJson(`/api/surveys/${survey.value!.id}/draft`, { answers: value })
      .then(() => { draftSaved.value = true; saveStatus.value = 'synced' })
      .catch(() => { draftSaved.value = true; saveStatus.value = navigator.onLine ? 'local' : 'offline' }), 700)
  }
  if (current.value >= visibleQuestions.value.length) current.value = Math.max(0, visibleQuestions.value.length - 1)
}, { deep: true })

watch(fillMode, async (value) => {
  if (!survey.value) return
  localStorage.setItem(`lumi-fill-mode-${survey.value.id}`, value)
  navigatorOpen.value = false
  await nextTick()
  if (value === 'scroll') {
    document.getElementById(`question-${question.value?.id}`)?.scrollIntoView({ block: 'start' })
  } else {
    questionStage.value?.scrollTo({ top: 0, behavior: 'auto' })
  }
})

function setAnswerFor(item: SurveyQuestion, value: unknown) {
  answers.value[item.id] = value
  const index = visibleQuestions.value.findIndex((candidate) => candidate.id === item.id)
  if (index >= 0) current.value = index
  error.value = ''
  if (survey.value?.definition?.presentation?.positiveFeedback !== false) playTone('select')
  if (survey.value?.definition?.presentation?.positiveFeedback !== false) {
    feedbackBurst.value = false
    requestAnimationFrame(() => { feedbackBurst.value = true; window.setTimeout(() => feedbackBurst.value = false, 500) })
  }
}

async function jumpTo(index: number, preserveError = false) {
  if (index < 0 || index >= total.value) return
  if (!preserveError) error.value = ''
  if (index !== current.value && survey.value?.definition?.presentation?.positiveFeedback !== false) playTone('next')
  current.value = index
  navigatorOpen.value = false
  await nextTick()
  if (fillMode.value === 'scroll') {
    document.getElementById(`question-${visibleQuestions.value[index]?.id}`)?.scrollIntoView({ block: 'start', behavior: preferences.reducedMotion ? 'auto' : 'smooth' })
  } else {
    questionStage.value?.scrollTo({ top: 0, behavior: 'auto' })
    animate('.paged-question-wrap .question-card', { opacity: [0, 1], y: [10, 0] }, { duration: preferences.reducedMotion || experienceMode.value === 'neutral' ? 0.01 : 0.28, ease: [0.22, 1, 0.36, 1] })
  }
}

async function move(direction: 1 | -1) {
  if (direction === 1 && current.value === total.value - 1) return submit()
  await jumpTo(Math.max(0, Math.min(total.value - 1, current.value + direction)))
}

async function submit() {
  if (!survey.value || submitting.value) return
  if (incompleteRequiredIndexes.value.length) {
    error.value = `还有 ${incompleteRequiredIndexes.value.length} 道必答题未完成，已带你跳到第一道遗漏题。`
    await jumpTo(incompleteRequiredIndexes.value[0], true)
    await nextTick()
    const currentContainer = document.getElementById(`question-${visibleQuestions.value[current.value]?.id}`) ?? answerArea.value
    const firstIncompleteRow = Array.from(currentContainer?.querySelectorAll<HTMLElement>('.matrix-row') ?? [])
      .find((row) => !row.querySelector('input:checked'))
    ;(firstIncompleteRow?.querySelector<HTMLInputElement>('input') ?? currentContainer?.querySelector<HTMLInputElement>('input, textarea, button'))?.focus({ preventScroll: true })
    return
  }
  submitting.value = true
  error.value = ''
  try {
    const result = await postJson<{ responseId: string; resultToken?: string; crisis?: { severity: string; resourcesRequired: boolean } | null }>(`/api/surveys/${survey.value.id}/responses`, {
      answers: answers.value,
      variant: variant.value,
      durationSeconds: Math.round((Date.now() - startedAt) / 1000),
      metadata: {
        startedAt: new Date(startedAt).toISOString(),
        questionOrder: visibleQuestions.value.map((item) => item.id),
        resumedFromDraft: resumedFromDraft.value,
        sessionEntries: sessionEntries.value,
        collectionChannel: 'online',
        fillMode: fillMode.value,
      },
    })
    localStorage.removeItem(storageKey.value)
    localStorage.removeItem(`lumi-session-entries-${survey.value.id}`)
    dirty.value = false
    completed.value = true
    if (survey.value.definition?.presentation?.positiveFeedback !== false) playTone('complete')
    if (result.crisis) {
      crisis.value = result.crisis
      pendingResult.value = result
      crisisResources.value = await api('/api/public/crisis-resources')
      return
    }
    await router.push({ path: `/results/${result.responseId}`, query: result.resultToken ? { token: result.resultToken } : {} })
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '提交失败，请检查网络后重试。'
  } finally { submitting.value = false }
}

onBeforeUnmount(() => {
  window.clearTimeout(saveTimer)
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('keydown', handleKeyboard)
})

async function continueAfterCrisis() {
  if (!pendingResult.value) return
  await router.push({ path: `/results/${pendingResult.value.responseId}`, query: pendingResult.value.resultToken ? { token: pendingResult.value.resultToken } : {} })
}
</script>

<template>
  <div v-if="!survey || !question" class="page-loading immersive-loading"><span class="loader-orb"/><p>{{ error || '正在铺开问卷…' }}</p></div>
  <div v-else :class="['take-page', `take-mode-${experienceMode}`]" :style="{ '--accent': survey.accent }">
    <header class="take-header">
      <RouterLink :to="`/surveys/${survey.slug}`" class="take-close" aria-label="退出问卷" @click="confirmExit"><X /></RouterLink>
      <div class="take-title"><span>{{ survey.title }}</span><small>{{ experienceMode === 'neutral' ? '量域中立模式' : '心光体验模式' }} · 实验界面 {{ variant }}</small></div>
      <div class="take-tools">
        <button class="take-fill-mode-toggle" :aria-label="fillMode === 'paged' ? '切换为连续滚动填写' : '切换为逐题翻页填写'" :title="fillMode === 'paged' ? '当前：逐题翻页' : '当前：连续滚动'" @click="fillMode = fillMode === 'paged' ? 'scroll' : 'paged'"><Rows3/><span>{{ fillMode === 'paged' ? '逐题' : '滚动' }}</span></button>
        <button class="take-navigator-toggle" :aria-label="navigatorOpen ? '收起题号与进度' : '打开题号与进度'" :aria-expanded="navigatorOpen" title="题号与进度" @click="navigatorOpen = !navigatorOpen"><ListChecks/></button>
        <button class="take-font" aria-label="更换全站字体" title="字体衣橱" @click="openFontLibrary"><Type /></button>
        <button class="take-sound" aria-label="打开声音与私人音乐库" @click="openMusicLibrary"><Volume2 v-if="music.playing || preferences.sound"/><VolumeX v-else/></button>
      </div>
    </header>
    <div class="progress-track"><span :style="{ width: `${progress}%` }"/></div>
    <main ref="questionStage" :class="['question-stage', `${fillMode}-mode`]">
      <div class="stage-aura"/><div class="stage-grid"/>
      <p v-if="draftSaved" :class="['draft-saved-notice', saveStatus]" role="status">{{ saveStatusText }}</p>
      <div v-if="feedbackBurst" class="feedback-burst" aria-hidden="true"><i v-for="n in 8" :key="n" :style="{ '--i': n }"/></div>
      <aside :class="['question-navigator', { open: navigatorOpen }]" aria-label="题号与填写进度">
        <div class="question-navigator-heading"><div><span>答题地图</span><strong>{{ answeredCount }}<small>/ {{ total }}</small></strong></div><button class="question-navigator-close" type="button" aria-label="收起题号与进度" @click="navigatorOpen = false"><X/></button></div>
        <div class="question-navigator-progress" :aria-label="`已完成 ${answeredCount}，共 ${total} 题`"><span><i :style="{ width: `${progress}%` }"/></span><b>{{ Math.round(progress) }}% 已完成</b></div>
        <p class="question-navigator-hint">{{ fillMode === 'paged' ? '逐题翻页，也可随时跳题；提交前会检查必答题。' : '上下滚动连续填写，也可用题号快速定位。' }}</p>
        <div class="question-navigator-list" role="navigation" aria-label="题号跳转">
          <button v-for="(item, index) in visibleQuestions" :key="item.id" :class="{ current: index === current, answered: questionComplete(item), required: item.required }" :aria-current="index === current ? 'step' : undefined" :aria-label="`第 ${index + 1} 题${item.required ? '，必答' : ''}${questionComplete(item) ? '，已完成' : '，未完成'}`" :title="`跳到第 ${index + 1} 题`" @click="jumpTo(index)">{{ String(index + 1).padStart(2, '0') }}</button>
        </div>
        <button class="question-navigator-submit" type="button" :disabled="submitting" @click="submit">{{ incompleteRequiredIndexes.length ? `检查 ${incompleteRequiredIndexes.length} 道遗漏题` : '完成并提交' }}<ArrowRight/></button>
      </aside>
      <div v-if="fillMode === 'paged'" ref="answerArea" class="paged-question-wrap">
        <SurveyQuestionRenderer :question="question" :index="current" :total="total" :dimensions="survey.definition?.dimensions" :model-value="answers[question.id]" :variant="variant" :error="error" instance-id="paged" @update:model-value="setAnswerFor(question, $event)"/>
      </div>
      <div v-else ref="answerArea" class="question-stream" aria-label="连续滚动问卷">
        <SurveyQuestionRenderer v-for="(item, index) in visibleQuestions" :id="`question-${item.id}`" :key="item.id" :question="item" :index="index" :total="total" :dimensions="survey.definition?.dimensions" :model-value="answers[item.id]" :variant="variant" :error="current === index ? error : ''" :instance-id="`scroll-${index}`" @update:model-value="setAnswerFor(item, $event)"/>
      </div>
    </main>

    <footer class="take-footer">
      <button v-if="fillMode === 'paged'" class="back-button" :disabled="current === 0" @click="move(-1)"><ChevronLeft/>上一题</button>
      <button v-else class="back-button scroll-top-button" @click="questionStage?.scrollTo({ top: 0, behavior: 'smooth' })"><ChevronLeft/>回到顶部</button>
      <span class="answer-status" :class="{ ready: canContinue }"><i :class="{ done: canContinue }"/><span>{{ continuationHint }}</span><small>总进度 {{ answeredCount }} / {{ total }}</small></span>
      <button class="button button-dark next-button" :disabled="submitting" @click="fillMode === 'paged' ? move(1) : submit()">{{ submitting ? '正在生成结果…' : fillMode === 'scroll' ? '完成并检查' : current === total - 1 ? '完成并查看结果' : '下一题' }}<ArrowRight v-if="!submitting"/></button>
    </footer>
    <div v-if="crisis && crisisResources" class="crisis-overlay" role="alertdialog" aria-modal="true"><section><ShieldAlert/><p class="eyebrow">YOUR SAFETY MATTERS</p><h2>请先照顾此刻的安全</h2><p>{{ crisisResources.immediateDanger }}</p><a :href="`tel:${crisisResources.hotline.phone}`"><strong>{{ crisisResources.hotline.phone }}</strong><span>{{ crisisResources.hotline.name }}</span></a><small>{{ crisisResources.note }}</small><button class="button button-dark" @click="continueAfterCrisis">我已看到，继续查看结果</button></section></div>
  </div>
</template>
